import { useState } from 'react';
import { FileText, Check, FolderOpen } from 'lucide-react';

import { cn } from '@/lib/utils';
import type {
  AssistantDocumentTemplateSelection,
  AssistantDocumentTemplateCandidate,
} from '@/lib/api-client';

interface DocumentTemplateSelectionCardProps {
  selection: AssistantDocumentTemplateSelection;
  onSelect: (candidate: AssistantDocumentTemplateCandidate) => void;
}

/**
 * Card de escolha de MODELO de documento (select_document_template).
 *
 * Mesma mecanica do ClientSelectionCard — e nao a do TransactionSelectionCard:
 * ao clicar, a escolha volta para o chat como mensagem do usuario, porque o
 * modelo precisa continuar o fluxo (inspecionar as variaveis, perguntar o que
 * falta, propor a geracao). No card de lancamento o id nunca volta ao modelo
 * porque a acao e destrutiva e nada continua depois do clique.
 */
export function DocumentTemplateSelectionCard({
  selection,
  onSelect,
}: DocumentTemplateSelectionCardProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  function handleClick(candidate: AssistantDocumentTemplateCandidate) {
    if (selectedId) return;
    setSelectedId(candidate.id);
    onSelect(candidate);
  }

  const hidden = selection.totalFound - selection.candidates.length;

  return (
    <div className="rounded-lg border border-[color-mix(in_oklch,var(--accent)_30%,var(--border))] bg-[var(--bg)] overflow-hidden">
      {/* Cabecalho */}
      <div className="flex items-center gap-1.5 border-b border-[var(--border)] px-3 py-2 text-xs font-semibold text-[var(--fg)] bg-[color-mix(in_oklch,var(--accent)_8%,transparent)]">
        <FileText className="h-3.5 w-3.5 text-[var(--accent)]" />
        Mais de um modelo encontrado · escolha um
      </div>

      {/* Candidatos */}
      <div className="flex flex-col divide-y divide-[var(--border)]">
        {selection.candidates.map((candidate) => {
          const isSelected = selectedId === candidate.id;
          const isLocked = selectedId !== null;

          return (
            <button
              key={candidate.id}
              type="button"
              onClick={() => handleClick(candidate)}
              disabled={isLocked}
              className={cn(
                'flex flex-col items-start gap-1 px-3 py-2.5 text-left text-xs transition-colors',
                isSelected && 'bg-[color-mix(in_oklch,#16a34a_10%,transparent)]',
                !isLocked && 'hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)] cursor-pointer',
                isLocked && !isSelected && 'opacity-50 cursor-not-allowed',
              )}
            >
              <div className="flex w-full items-start gap-1.5">
                {isSelected ? (
                  <Check className="h-3.5 w-3.5 shrink-0 text-green-600 mt-px" />
                ) : (
                  <FileText className="h-3.5 w-3.5 shrink-0 text-[var(--fg-muted)] mt-px" />
                )}
                <span className="font-semibold text-[var(--fg)] break-words">
                  {candidate.name}
                </span>
              </div>

              {candidate.categoryName && (
                <span className="inline-flex items-center gap-1 text-[var(--fg-muted)]">
                  <FolderOpen className="h-3 w-3 shrink-0" />
                  {candidate.categoryName}
                </span>
              )}

              {candidate.description && (
                <span className="text-[var(--fg-muted)] break-words line-clamp-2">
                  {candidate.description}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {hidden > 0 && (
        <div className="border-t border-[var(--border)] px-3 py-1.5 text-xs text-[var(--fg-muted)] italic">
          +{hidden} outro(s) modelo(s). Refine o nome se nenhum destes servir.
        </div>
      )}
    </div>
  );
}
